import type { toast } from "@minket/ui/components/sonner";
import { useSyncExternalStore } from "react";

/**
 * Toasts, with the toaster (sonner) loaded on the first one instead of on every page. Call
 * `notify("success", "Key revoked.")` after a mutation; the root mounts the toaster the first
 * time (`useToasterWanted`), and sonner replays a toast created before its toaster subscribed.
 * Toasts confirm what just happened; errors a person must act on stay on the page.
 */

type Toast = typeof toast;
export type ToastKind = "message" | "success" | "info" | "warning" | "error";

const loadToaster = () => import("@minket/ui/components/sonner");

let wanted = false;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True once a toast has been asked for; the root then mounts the toaster. */
export function useToasterWanted(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => wanted,
    () => false,
  );
}

export function notify<K extends ToastKind>(kind: K, ...args: Parameters<Toast[K]>): void {
  if (!wanted) {
    wanted = true;
    for (const listener of listeners) listener();
  }
  void loadToaster().then((module) => {
    const show = module.toast[kind] as (...values: Parameters<Toast[K]>) => unknown;
    show(...args);
  });
}
